import { useState } from "react";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "./ui/alert-dialog";

interface DeleteConfirmDialogProps {
  open: boolean;
  /** e.g. "SSP", "Component", "Catalog" */
  type: string;
  /** Name of the item being deleted */
  name: string;
  /** Whether the delete action is in progress */
  deleting: boolean;
  /** Show a reason/comment field */
  showReason?: boolean;
  onCancel: () => void;
  onConfirm: (reason?: string) => void;
}

export function DeleteConfirmDialog({
  open,
  type,
  name,
  deleting,
  showReason = true,
  onCancel,
  onConfirm,
}: DeleteConfirmDialogProps) {
  const [reason, setReason] = useState("");

  return (
    <AlertDialog open={open} onOpenChange={(v) => !v && onCancel()}>
      <AlertDialogContent className="text-foreground">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-foreground">
            Delete {type}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-foreground">
            Are you sure you want to delete{" "}
            <strong className="text-foreground break-words break-all whitespace-normal">"{name}"</strong>? This action
            cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        {showReason && (
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-foreground">
              Reason (optional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter a reason for deletion"
              rows={2}
              className="w-full px-3 py-2 text-sm text-foreground rounded-lg border border-border bg-card outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all resize-none placeholder:d/50"
            />
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting} onClick={onCancel}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={deleting}
            onClick={() => {
              onConfirm(reason || undefined);
              setReason("");
            }}
            className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
          >
            {deleting ? (
              <span className="flex items-center gap-2">
                <Loader2 className="size-3.5 animate-spin" />
                Deleting
              </span>
            ) : (
              "Delete"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
